import { enums, useClient, useQuery } from '@weenat/client'
import { Org } from '@weenat/client/dist/resources/orgs'
import { isNil } from 'lodash-es'
import { createContext, useContext } from 'react'
import { useParams } from './routx-router'
import { clearAllStates } from './src/authentication/components/Logout'
import { useLoginAs, useToken } from './state'

const EMPTY_ORGS: Org[] = []

export const CURRENT_ORG_KEY = '@currentOrgId'

type IOrgContext = Org

const OrgContext = createContext<IOrgContext | null>(null)

export const useDefaultOrg = () => {
  const client = useClient()
  const [token] = useToken()
  const [loginAs] = useLoginAs()
  let orgId: number | undefined = undefined

  const { farmId: pathParamOrgId } = useParams()

  const config = {
    enabled: !isNil(token)
  }

  const myProfileRequest = useQuery(
    client.me.getMyProfile({ loginAsEmail: loginAs?.email }),
    config
  )

  const allOrgsFromUserRequest = useQuery(client.orgs.getAllPages(), config)
  const allOrgsFromUser = allOrgsFromUserRequest.data ?? EMPTY_ORGS
  const firstAccessibleOrgId = allOrgsFromUser
    .sort((a) =>
      [enums.BillingStatus.plus, enums.BillingStatus.expert].includes(a.billingStatus) ? -1 : 1
    )
    .sort((a, b) => a.id - b.id)
    .at(0)?.id

  if (isNil(pathParamOrgId)) {
    const storedCurrentOrgId = localStorage.getItem(CURRENT_ORG_KEY)
    if (!isNil(storedCurrentOrgId)) {
      orgId = parseInt(storedCurrentOrgId, 10)
    } else {
      const favoriteOrgId = myProfileRequest.data?.favoriteOrgId
      if (!isNil(favoriteOrgId)) {
        orgId = favoriteOrgId
      } else {
        orgId = firstAccessibleOrgId
      }
    }
  } else {
    orgId = pathParamOrgId
  }

  const orgRequest = useQuery(client.orgs.get(orgId), {
    enabled: !isNil(orgId) && !isNil(token)
  })

  /**
   * if previous org is no more accessible we defaulting on the first one accessible
   * case like i put a favorite than loose access to it or last org i was using is no more acessible
   */
  const alwaysAvailableOrgRequest = useQuery(client.orgs.get(firstAccessibleOrgId), {
    enabled: !isNil(firstAccessibleOrgId) && !isNil(token) && orgRequest.isError
  })

  const isLoading =
    allOrgsFromUserRequest.isLoading ||
    orgRequest.isLoading ||
    myProfileRequest.isLoading ||
    orgRequest.isLoading ||
    alwaysAvailableOrgRequest.isLoading

  const unAuthorized = myProfileRequest.error?.status === 403

  const errorOnUserFetching = myProfileRequest.isError
  const unableToGetOwnIdentity = errorOnUserFetching || unAuthorized

  if (unableToGetOwnIdentity) {
    // user is unauthorized to get is own identity or org so we should disconnect him
    clearAllStates(loginAs !== null)
  }

  const org = orgRequest.data ?? alwaysAvailableOrgRequest.data
  return { org, isLoading }
}

export function useOrgContext() {
  const context = useContext<IOrgContext | null>(OrgContext)

  if (!context) {
    throw new Error(
      'You may have forgot to render <OrgContextProvider /> because OrgContext is not defined'
    )
  }
  return context
}

interface OrgContextProviderProps {
  org: Org
}

export const OrgContextProvider: FC<OrgContextProviderProps> = ({ org, children }) => {
  localStorage.setItem(CURRENT_ORG_KEY, `${org.id}`)
  return <OrgContext.Provider value={org}>{children}</OrgContext.Provider>
}
